import React, { useState, useRef, useEffect } from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { projectToWorld } from "./projectToWorld";
import { useFloorplannerStore } from '../../store/floorplannerStore';
import { observer } from "mobx-react-lite";
import { editorStore } from "../../store/editorStore";
import { renderStore } from "../../store/renderStore";
import { SymbolType } from "../../types/floorplannerTypes";
import {
  isLoose2DDragCollisionSymbol,
  resolveSymbol2DDragPosition,
} from "../../utils/symbol2DDragCollision";

interface DraggableObjectProps {
  position: [number, number];
  onDragStart: (startPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => [number, number] | void;
  selectable: boolean;
  rotation?: [number, number, number];
  children?: React.ReactNode;
  disableDrag?: boolean;
  attachmentType?: 'doorAttachments' | 'windowAttachments' | 'voidAttachments' | 'noAttachments';
  attachmentId?: string;
  zIndex?: number;
  symbol?: SymbolType
}

const positionsEqual = (a: [number, number], b: [number, number]): boolean =>
  a[0] === b[0] && a[1] === b[1];

/**
 * A draggable object that can be moved around the canvas.
 * 
 * @param position The position of the object.
 * @param onDragStart The function to call when the object starts dragging.
 * @param onDragEnd The function to call when the object stops dragging.
 * @param onDrag The function to call when the object is dragged.
 * @param selectable Whether the object is selectable.
 * @param rotation The rotation of the object.
 * @param children The children to render.
 * @param disableDrag Whether to disable dragging.
 * @param attachmentType The type of attachment.
 * @param attachmentId The ID of the attachment.
 * @returns The DraggableObject component.
 * 
 **/
const DraggableObject: React.FC<DraggableObjectProps> = observer(({
  position,
  onDragStart,
  onDragEnd,
  onDrag,
  rotation,
  selectable,
  children,
  disableDrag = false,
  attachmentType,
  attachmentId,
  zIndex = 0,
  symbol
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const attachableRef = useRef<{
    checkForAttachment: (fromPosition: [number, number]) => { position: [number, number], wallId: string } | undefined,
    findNearestWall: (fromPosition: [number, number], preferredWallId?: string) => { position: [number, number], wallId: string } | undefined,
    setPreferredWall?: (wallId: string | null) => void,
    clearPreferredWall?: () => void
  }>(null);
  const [isHovered, setIsHovered] = useState(false);
  const dragging = useRef(false);
  const dragOffset = useRef<[number, number] | null>(null);
  const rafId = useRef<number | null>(null);
  const pendingPointerPosition = useRef<{ clientX: number; clientY: number } | null>(null);
  const { camera, gl } = useThree();
  // We use a local position state for the dragging effect, then when the drag ends we update the store
  const [localPosition, setLocalPosition] = useState(position);
  const localPositionRef = useRef<[number, number]>(position);
  const attachmentCommitPositionRef = useRef<[number, number] | null>(null);
  const lastValidCollisionPositionRef = useRef<THREE.Vector2 | null>(null);
  const floorplannerStore = useFloorplannerStore();
  const aboutToDrag = useRef(false);
  // Track the wall the symbol was attached to during drag for finalization
  const draggedAttachmentWallRef = useRef<string | null>(null);
  const initialAttachmentWallRef = useRef<string | null>(null);
  const shouldCommitToStoreDuringDrag =
    attachmentType === 'doorAttachments' ||
    attachmentType === 'windowAttachments' ||
    attachmentType === 'voidAttachments';

  const restoreSuppressedAttachmentOutlineGap = () => {
    if (attachmentId && initialAttachmentWallRef.current) {
      renderStore.restoreAttachmentOutlineGap(initialAttachmentWallRef.current, attachmentId);
    }
  };

  const processPointerMove = (clientX: number, clientY: number) => {
    if (disableDrag) return;
    if (dragging.current && dragOffset.current) {
      if (aboutToDrag.current) {
        floorplannerStore.beginUndoTransaction('dragObject');
        if (symbol) {
          floorplannerStore.recordSymbolForUndo(symbol.id);
        } else if (attachmentId) {
          // Fallback if symbol object not passed but ID is known (e.g. for simple attachments)
          // Try to determine type or assume symbol if attached?
          // Actually most usages pass 'symbol'.
          if (floorplannerStore.symbolsMap.has(attachmentId)) {
            floorplannerStore.recordSymbolForUndo(attachmentId);
          }
        }
        if (shouldCommitToStoreDuringDrag && initialAttachmentWallRef.current) {
          floorplannerStore.recordWallForUndo(initialAttachmentWallRef.current);
        }
        aboutToDrag.current = false;
      }
      const [newX, newY] = projectToWorld(clientX, clientY, gl, camera);
      const [offsetX, offsetY] = dragOffset.current;
      const rawPosition: [number, number] = [newX - offsetX, newY - offsetY];

      // Track the final position to use (may be snapped)
      const dragResult = onDrag(rawPosition);
      let finalPosition: [number, number] = Array.isArray(dragResult) ? dragResult : rawPosition;

      if (shouldCommitToStoreDuringDrag) {
        const preferredWallId = draggedAttachmentWallRef.current || initialAttachmentWallRef.current || undefined;
        const nearestWall = attachableRef.current?.findNearestWall?.(rawPosition, preferredWallId);

        if (nearestWall && attachmentId) {
          draggedAttachmentWallRef.current = nearestWall.wallId;
          attachmentCommitPositionRef.current = nearestWall.position;
          const preview = floorplannerStore.getSymbolAttachmentPreview(attachmentId, nearestWall.wallId, nearestWall.position);
          if (preview) {
            finalPosition = [preview.position.x, preview.position.y];
            renderStore.setAttachmentDragPreview(attachmentId, {
              wallId: nearestWall.wallId,
              position: preview.position,
              rotation: preview.rotation,
              positionFromStart: preview.positionFromStart,
            });
          } else {
            finalPosition = nearestWall.position;
          }
        } else {
          draggedAttachmentWallRef.current = null;
          attachmentCommitPositionRef.current = rawPosition;
          if (attachmentId) {
            renderStore.setAttachmentDragPreview(attachmentId, {
              wallId: null,
              position: new THREE.Vector2(rawPosition[0], rawPosition[1]),
              rotation: symbol?.rotation ?? 0,
            });
          }
        }
      } else if (
        symbol &&
        isLoose2DDragCollisionSymbol(symbol) &&
        !floorplannerStore.symbolIsAttached(symbol.id)
      ) {
        const bypassCollision = editorStore.isShiftPressed;
        const candidatePosition = new THREE.Vector2(finalPosition[0], finalPosition[1]);
        const lastValidPosition =
          lastValidCollisionPositionRef.current ??
          new THREE.Vector2(position[0], position[1]);
        const nextPosition = resolveSymbol2DDragPosition({
          symbol,
          candidatePosition,
          lastValidPosition,
          walls: Array.from(floorplannerStore.wallsMap.values()),
          singleLines: Array.from(floorplannerStore.singleLinesMap.values()),
          wallShapesById: renderStore.innerWallShapesMap,
          defaultWallWidth: floorplannerStore.wallWidth,
          defaultSingleLineWidth: Math.max(
            floorplannerStore.convertLineWeightToWorld(
              floorplannerStore.singleLineWeight,
            ),
            0.08,
          ),
          wallSnapTolerance: editorStore.interactionSnappingTolerance(
            editorStore.snappingTolerance * 2,
          ),
          wallSnapReferencePosition: new THREE.Vector2(position[0], position[1]),
          bypassCollision,
        });
        if (!bypassCollision) {
          lastValidCollisionPositionRef.current = nextPosition.clone();
        }
        finalPosition = [nextPosition.x, nextPosition.y];
      }

      // Set localPosition AFTER attachment check to use the correct (possibly snapped) position
      // This prevents flickering between raw and snapped positions
      localPositionRef.current = finalPosition;
      setLocalPosition((currentPosition) =>
        positionsEqual(currentPosition, finalPosition) ? currentPosition : finalPosition
      );
    }
  };

  const flushPendingPointerMove = () => {
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }

    const pending = pendingPointerPosition.current;
    pendingPointerPosition.current = null;

    if (pending) {
      processPointerMove(pending.clientX, pending.clientY);
    }
  };

  const handlePointerMove = (e: PointerEvent) => {
    if (disableDrag) return;
    if (!dragging.current || !dragOffset.current) return;

    pendingPointerPosition.current = { clientX: e.clientX, clientY: e.clientY };

    if (rafId.current !== null) {
      return;
    }

    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      const pending = pendingPointerPosition.current;
      pendingPointerPosition.current = null;

      if (pending) {
        processPointerMove(pending.clientX, pending.clientY);
      }
    });
  };

  const handlePointerUp = (e: PointerEvent) => {
    if (disableDrag) return;
    try {
      if (gl.domElement.hasPointerCapture(e.pointerId)) {
        gl.domElement.releasePointerCapture(e.pointerId);
      }
    } catch {
      // Pointer capture can be absent if the browser cancelled the pointer.
    }
    aboutToDrag.current = false;
    flushPendingPointerMove();
    if (dragging.current && dragOffset.current) {
      const [newX, newY] = projectToWorld(e.clientX, e.clientY, gl, camera);
      const [offsetX, offsetY] = dragOffset.current;
      const rawEndPosition: [number, number] = [newX - offsetX, newY - offsetY];
      const finalPosition = localPositionRef.current ?? rawEndPosition;
      const commitPosition = shouldCommitToStoreDuringDrag
        ? attachmentCommitPositionRef.current ?? finalPosition
        : finalPosition;

      if (shouldCommitToStoreDuringDrag && attachmentId) {
        const initialWallId = initialAttachmentWallRef.current;
        const targetWallId = draggedAttachmentWallRef.current;
        const attachmentWallIdsToRecord = new Set<string>();
        if (initialWallId) attachmentWallIdsToRecord.add(initialWallId);
        if (targetWallId) attachmentWallIdsToRecord.add(targetWallId);

        restoreSuppressedAttachmentOutlineGap();

        if (initialWallId && targetWallId === initialWallId) {
          floorplannerStore.updateSymbolAttachmentPosition(attachmentId, initialWallId, commitPosition, false);
        } else {
          if (initialWallId) {
            floorplannerStore.detachSymbolFromWall(attachmentId);
          }

          if (targetWallId) {
            if (targetWallId !== initialWallId) {
              floorplannerStore.recordWallForUndo(targetWallId);
            }
            floorplannerStore.attachSymbolToWall(attachmentId, targetWallId, commitPosition);
          } else {
            floorplannerStore.updateSymbolProperty(attachmentId, "position", commitPosition);
          }
        }
        renderStore.clearAttachmentDragPreview(attachmentId);
        attachmentWallIdsToRecord.forEach((wallId) => {
          floorplannerStore.recordWallChange(wallId, 'modify');
        });
      } else {
        // If the object is not attached to a wall, update the position in the store
        if (!floorplannerStore.symbolIsAttached(attachmentId || '')) {
          floorplannerStore.updateSymbolProperty(attachmentId || '', "position", finalPosition);
        } else {
          // OPTIMIZATION: Finalize the attachment position to perform expensive geometry operations
          // that were skipped during lightweight drag updates
          const attachedWallId = draggedAttachmentWallRef.current;
          if (attachedWallId && attachmentId) {
            floorplannerStore.finalizeSymbolAttachmentPosition(attachmentId, attachedWallId);
          }
        }
      }
      onDragEnd(finalPosition);
      // Select the symbol again if it is selectable
      if (symbol && selectable && !symbol.selected) {
        if (!editorStore.isShiftPressed) {
          editorStore.clearSelections();
        }
        editorStore.addSelection(symbol);
        floorplannerStore.updateSymbolProperty(symbol.id, "selected", true);
      }

      // Record final state and commit transaction
      if (symbol) {
        floorplannerStore.recordSymbolChange(symbol.id, 'modify');
      } else if (attachmentId && floorplannerStore.symbolsMap.has(attachmentId)) {
        floorplannerStore.recordSymbolChange(attachmentId, 'modify');
      }
      floorplannerStore.pushToUndoStack();
    }
    dragging.current = false;
    dragOffset.current = null;
    pendingPointerPosition.current = null;
    draggedAttachmentWallRef.current = null;
    initialAttachmentWallRef.current = null;
    attachmentCommitPositionRef.current = null;
    lastValidCollisionPositionRef.current = null;
    // Remove event listeners from the canvas
    gl.domElement.removeEventListener("pointermove", handlePointerMove);
    gl.domElement.removeEventListener("pointerup", handlePointerUp);
    gl.domElement.removeEventListener("pointercancel", handlePointerUp);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.setDirty();
  };

  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    if (disableDrag) return;
    // Do not interact if in construction mode
    if (editorStore.areaConstructionMode || editorStore.areaFillConstructionMode || editorStore.areaBleedFillMode || editorStore.rulerConstructionMode || editorStore.wallConstructionMode || editorStore.lineConstructionMode) {
      return;
    }

    // View-only: select to inspect measurements, but don't start a drag.
    if (floorplannerStore.lockedByOther) {
      if (symbol && selectable) {
        e.stopPropagation();
        if (!editorStore.isShiftPressed) {
          editorStore.clearSelections();
        }
        editorStore.addSelection(symbol);
        floorplannerStore.updateSymbolProperty(symbol.id, "selected", true);
      }
      return;
    }

    // Check if this object is part of a group and if the group is not fully selected
    let shouldSelectGroup = false;
    if (symbol && selectable && !symbol.selected) {
      const groupId = floorplannerStore.getObjectGroup(symbol.id);
      if (groupId) {
        // Check if all group members are selected
        const groupObjects = floorplannerStore.getGroupObjects(groupId);
        const allGroupSelected = groupObjects.every(obj =>
          editorStore.selections.some(sel => sel.id === obj.id)
        );
        shouldSelectGroup = !allGroupSelected;
      }
    }

    // If this object needs group selection, don't start individual drag - let SelectableSymbol handle it
    if (shouldSelectGroup) {
      return; // Let the event bubble up to SelectableSymbol for group handling
    }

    // If we have multiple selections, only start individual drag if this object is not selected
    if (editorStore.selections.length > 1) {
      // Only return early if this object is part of the current selection (group drag scenario)
      if (symbol && symbol.selected) {
        return; // Let group drag system handle it by not starting individual drag
      }
      // If this object is not selected, continue with normal selection logic below
    }

    aboutToDrag.current = true;
    // Check if the cursor class exists
    const cursorClassExist = document.body.classList.contains("cursor-vector");
    e.stopPropagation();
    // Restore the cursor if it is not the default cursor since stopPropagation will reset the body class
    if (cursorClassExist) {
      document.body.classList.add("cursor-vector");
    }
    // Select the symbol again if it is selectable
    if (symbol && selectable && !symbol.selected) {
      if (!editorStore.isShiftPressed) {
        editorStore.clearSelections();
      }
      editorStore.addSelection(symbol);
      floorplannerStore.updateSymbolProperty(symbol.id, "selected", true);
    }
    dragging.current = true;
    try {
      gl.domElement.setPointerCapture(e.pointerId);
    } catch {
      // Older/event-emulated environments may not support pointer capture.
    }
    const [worldX, worldY] = projectToWorld(e.clientX, e.clientY, gl, camera);
    const offsetX = worldX - position[0];
    const offsetY = worldY - position[1];
    dragOffset.current = [offsetX, offsetY];
    localPositionRef.current = position;
    lastValidCollisionPositionRef.current = new THREE.Vector2(position[0], position[1]);
    attachmentCommitPositionRef.current = position;
    if (shouldCommitToStoreDuringDrag && attachmentId) {
      const attachedWallId = floorplannerStore.symbolIsAttached(attachmentId) ?? null;
      initialAttachmentWallRef.current = attachedWallId;
      draggedAttachmentWallRef.current = attachedWallId;
      renderStore.setAttachmentDragPreview(attachmentId, {
        wallId: attachedWallId,
        position: new THREE.Vector2(position[0], position[1]),
        rotation: symbol?.rotation ?? 0,
      });
      if (attachedWallId) {
        renderStore.removeWallClipping(attachedWallId, attachmentId);
        renderStore.suppressAttachmentOutlineGap(attachedWallId, attachmentId);
        floorplannerStore.incrementWallGeometryVersion(attachedWallId);
        floorplannerStore.objectNeedsUpdate(attachedWallId);
        renderStore.generateOutlineSegments(attachedWallId);
        renderStore.requestInvalidation(attachedWallId);
      }
    }
    onDragStart([offsetX, offsetY]);

    // Attach event listeners to the canvas
    gl.domElement.addEventListener("pointermove", handlePointerMove);
    gl.domElement.addEventListener("pointerup", handlePointerUp);
    gl.domElement.addEventListener("pointercancel", handlePointerUp);
    floorplannerStore.setBlockDirty(true);
  };

  useEffect(() => {
    if (disableDrag) {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
      pendingPointerPosition.current = null;
      dragging.current = false;
      dragOffset.current = null;
      lastValidCollisionPositionRef.current = null;
      if (attachmentId) {
        renderStore.clearAttachmentDragPreview(attachmentId);
      }
      gl.domElement.removeEventListener("pointermove", handlePointerMove);
      gl.domElement.removeEventListener("pointerup", handlePointerUp);
      gl.domElement.removeEventListener("pointercancel", handlePointerUp);
      floorplannerStore.setBlockDirty(false);
    }
  }, [disableDrag, gl.domElement]);

  // Make sure that if the store have updated the position, we update the local position
  useEffect(() => {
    if (dragging.current) {
      return;
    }

    if (!positionsEqual(localPosition, position)) {
      localPositionRef.current = position;
      setLocalPosition(position);
    }
  }, [position[0], position[1], localPosition]);

  // Calculate translate offset for optimized group drag
  const translateOffset = symbol && renderStore.isObjectTranslating(symbol.id)
    ? renderStore.groupTranslateOffset
    : { x: 0, y: 0 };

  // Calculate rotation offset for optimized group rotation
  const isRotating = symbol && renderStore.isObjectRotating(symbol.id);
  const rotationOffset = isRotating
    ? { angle: renderStore.groupRotationAngle, center: renderStore.groupRotationCenter }
    : null;

  // Use position prop directly when NOT doing individual drag to avoid flicker on group translate end.
  // The localPosition state syncs via useEffect which runs after render, causing a one-frame lag
  // where objects would flash at original position when group translate ends.
  // Only use localPosition during individual drag for smooth visual updates.
  const effectivePosition = dragging.current ? localPosition : position;
  const attachmentDragPreview = symbol ? renderStore.getAttachmentDragPreview(symbol.id) : undefined;

  // Calculate final position and rotation with offsets applied
  let finalPosition = [effectivePosition[0] + translateOffset.x, effectivePosition[1] + translateOffset.y];
  const baseRotation: [number, number, number] = rotation ?? [0, 0, 0];
  let finalRotation = baseRotation;
  if (attachmentDragPreview && dragging.current && shouldCommitToStoreDuringDrag) {
    finalRotation = [
      baseRotation[0],
      baseRotation[1],
      baseRotation[2] + attachmentDragPreview.rotation - (symbol?.rotation ?? 0),
    ];
  }
  if (rotationOffset) {
    // Rotate position around rotation center
    const dx = effectivePosition[0] - rotationOffset.center.x;
    const dy = effectivePosition[1] - rotationOffset.center.y;
    const cos = Math.cos(rotationOffset.angle);
    const sin = Math.sin(rotationOffset.angle);
    finalPosition = [
      rotationOffset.center.x + dx * cos - dy * sin,
      rotationOffset.center.y + dx * sin + dy * cos
    ];
    // Add rotation angle to object rotation (z-axis)
    finalRotation = [finalRotation[0], finalRotation[1], finalRotation[2] + rotationOffset.angle];
  }

  return (
    <mesh
      ref={meshRef}
      position={[finalPosition[0], finalPosition[1], 0]}
      rotation={finalRotation}
      onPointerDown={handlePointerDown}
      onPointerOver={() => setIsHovered(true)}
      onPointerOut={() => setIsHovered(false)}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            ref: attachableRef as React.Ref<any>, // Type assertion to pass ref
          });
        }
        return child;
      })}
    </mesh>
  );
});

export default DraggableObject;
